import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { X, LogIn, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { libraryGlowButtonClass } from "@/features/library/lib/library-ui";
import { authPrimaryButtonClass } from "@/components/auth/auth-ui";
import { cn } from "@/lib/utils";
import { useAuthGateStore } from "../store/auth-gate-store";
import {
  persistAuthGateRedirect,
  readAuthGateRedirect,
} from "../utils/redirect";
import { isModifiedClick } from "@/lib/link-utils";
import harmonaiLogo from "@/assets/harmonyai-logo.svg";

/**
 * Auth Gate Dialog (Zero Props)
 * Public modda auth gerektiren bir aksiyon denendiğinde gösterilen
 * şık login/register popup'ı. App genelinde tek instance olarak mount edilir.
 */
export const AuthGateDialog: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { isOpen, title, description, closeAuthGate } = useAuthGateStore();

  const persistRedirectPath = () => {
    // requireAuth({ redirectTo }) hedefi zaten yazdıysa ezme — aksi halde
    // mağaza tıklaması landing'e dönerdi.
    if (readAuthGateRedirect()) return;
    persistAuthGateRedirect(location.pathname + location.search);
  };

  // Redirect adresi modifier'lı tıklamada (yeni sekme) da kaydedilmeli ki
  // yeni sekmede tamamlanan login akışı doğru sayfaya dönebilsin. Ama dialog'u
  // SADECE mevcut sekme login/register'a geçtiğinde kapatıyoruz — modifier'lı
  // tıklamada kullanıcı bu sayfada kalıyor, dialog açık kalmalı.
  const handleLoginClick = (e: React.MouseEvent) => {
    persistRedirectPath();
    if (isModifiedClick(e)) return;
    closeAuthGate();
  };

  const handleRegisterClick = (e: React.MouseEvent) => {
    persistRedirectPath();
    if (isModifiedClick(e)) return;
    closeAuthGate();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[1100] flex items-end sm:items-center justify-center p-0 sm:p-4">
          {/* Backdrop */}
          <motion.button
            type="button"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={closeAuthGate}
            className="absolute inset-0 bg-black/50 backdrop-blur-[3px]"
            aria-label={t("common.close")}
          />

          {/* Dialog Card */}
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="relative w-full sm:w-[420px] max-w-full rounded-t-3xl sm:rounded-3xl bg-bg-primary/90 dark:bg-gray-950/85 backdrop-blur-2xl border border-black/[0.06] dark:border-white/[0.08] shadow-2xl overflow-hidden pb-[env(safe-area-inset-bottom)]"
            role="dialog"
            aria-modal="true"
          >
            {/* Dekoratif üst gradient */}
            <div className="absolute top-0 inset-x-0 h-28 bg-gradient-to-b from-indigo-500/10 via-purple-500/5 to-transparent dark:from-indigo-400/15 dark:via-purple-400/5 pointer-events-none" />

            {/* Close - z-10: içerik div'i (relative) DOM'da sonra geldiği için
                z-index olmadan butonun üzerini kapatıyor ve tıklamayı engelliyordu */}
            <button
              type="button"
              onClick={closeAuthGate}
              className="absolute top-4 right-4 z-10 inline-flex items-center justify-center size-8 rounded-full text-text-tertiary-600 dark:text-gray-400 hover:text-text-primary dark:hover:text-gray-100 hover:bg-bg-secondary dark:hover:bg-gray-700 transition-colors"
              aria-label={t("common.close")}
            >
              <X className="size-4" />
            </button>

            <div className="relative px-6 pt-8 pb-6 sm:px-8 flex flex-col items-center text-center">
              {/* Harmony Logo */}
              <div className="relative mb-4 flex items-center justify-center size-14 rounded-2xl bg-black/[0.04] ring-1 ring-inset ring-black/[0.06] dark:bg-white/[0.06] dark:ring-white/[0.08]">
                <img
                  src={harmonaiLogo}
                  alt="HarmonyAI"
                  className="h-7 w-auto"
                />
              </div>

              {/* Title & Description */}
              <h2 className="text-lg font-semibold text-text-primary dark:text-white">
                {title || t("authGate.title")}
              </h2>
              <p className="mt-2 text-sm text-text-secondary dark:text-gray-300 leading-6 max-w-[320px]">
                {description || t("authGate.description")}
              </p>

              {/* Actions */}
              <div className="mt-6 w-full flex flex-col gap-2.5">
                <Button
                  asChild
                  variant="darkPrimary"
                  className={authPrimaryButtonClass}
                >
                  <Link to="/login" onClick={handleLoginClick}>
                    <LogIn className="size-4 mr-2" />
                    {t("authGate.login")}
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className={cn(
                    libraryGlowButtonClass,
                    "w-full h-11 rounded-xl font-medium border border-black/[0.08] bg-transparent text-foreground dark:border-white/[0.12] dark:text-white",
                  )}
                >
                  <Link to="/register" onClick={handleRegisterClick}>
                    <UserPlus className="size-4 mr-2" />
                    {t("authGate.register")}
                  </Link>
                </Button>
              </div>

              {/* Dismiss link */}
              <button
                type="button"
                onClick={closeAuthGate}
                className="mt-4 text-xs font-medium text-text-tertiary-600 dark:text-gray-400 hover:text-text-primary dark:hover:text-gray-200 transition-colors"
              >
                {t("authGate.continueBrowsing")}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
